<template>
  <div class="terms-of-service">
    <!-- Tab bar -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'customers' }"
        @click="activeTab = 'customers'"
      >
        <v-icon class="tab-icon">mdi-account-group-outline</v-icon>
        For customers
      </button>
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'business' }"
        @click="activeTab = 'business'"
      >
        <v-icon class="tab-icon">mdi-store-outline</v-icon>
        For businesses
      </button>
    </div>

    <!-- Business Terms Tab -->
    <div v-if="activeTab === 'business'">
      <BusinessTermsComponent />
    </div>

    <!-- Customer Terms Tab -->
    <div v-if="activeTab === 'customers'" class="content" :style="{ color: foregroundColor }">
      <ol>
        <li>
          <strong>{{ eligibilityHeading }}</strong>
          <ol type="1">
            <li>{{ eligibilityLine1 }}</li>
            <li>{{ eligibilityLine2 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ personalInfoHeading }}</strong>
          <ol type="1">
            <li>{{ personalInfoLine1 }}</li>
            <li>{{ personalInfoLine2 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ earningHeading }}</strong>
          <ol type="1">
            <li>{{ earningLine1 }}</li>
            <li>{{ earningLine2 }}</li>
            <li>{{ earningLine3 }}</li>
            <li>{{ earningLine4 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ restrictionsHeading }}</strong>
          <ol type="1">
            <li>{{ restrictionsLine1 }}</li>
            <li>{{ restrictionsLine2 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ terminationHeading }}</strong>
          <ol type="1">
            <li>{{ terminationLine1 }}</li>
            <li>{{ terminationLine2 }}</li>
            <li>{{ terminationLine3 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ liabilityHeading }}</strong>
          <ol type="1">
            <li>{{ liabilityLine1 }}</li>
            <li>{{ liabilityLine2 }}</li>
            <li>{{ liabilityLine3 }}</li>
            <li>{{ liabilityLine4 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ marketingHeading }}</strong>
          <ol type="1">
            <li>{{ marketingLine1 }}</li>
            <li v-html="marketingLine2"></li>
            <li>{{ marketingLine3 }}</li>
            <li>{{ marketingLine4 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ governingLawHeading }}</strong>
          <ol type="1">
            <li>{{ governingLawLine1 }}</li>
            <li>{{ governingLawLine2 }}</li>
          </ol>
        </li>
        <li>
          <strong>{{ contactHeading }}</strong>
          <p v-html="contactLine"></p>
        </li>
      </ol>

      <!-- Privacy Policy -->

      <h2>{{ privacyTitle }}</h2>
      <p><em>{{ effectiveDateLabel }} {{ effectiveDate }}</em></p>
      <ol>
        <li>
          <strong>{{ infoCollectHeading }}</strong>
          <ul>
            <li><strong>{{ personalInfoLabel }}</strong> {{ personalInfoDesc }}</li>
            <li><strong>{{ usageDataLabel }}</strong> {{ usageDataDesc }}</li>
            <li><strong>{{ commDataLabel }}</strong> {{ commDataDesc }}</li>
          </ul>
        </li>
        <li>
          <strong>{{ howUseHeading }}</strong>
          <ul>
            <li>{{ howUseLine1 }}</li>
            <li>{{ howUseLine2 }}</li>
            <li>{{ howUseLine3 }}</li>
            <li>{{ howUseLine4 }}</li>
            <li>{{ howUseLine5 }}</li>
          </ul>
        </li>
        <li>
          <strong>{{ privacyMarketingHeading }}</strong>
          <p>{{ privacyMarketingDesc }}</p>
        </li>
        <li>
          <strong>{{ sharingHeading }}</strong>
          <p>{{ sharingDesc }}</p>
          <ul>
            <li>{{ sharingCase1 }}</li>
            <li>{{ sharingCase2 }}</li>
          </ul>
        </li>
        <li>
          <strong>{{ dataRetentionHeading }}</strong>
          <p>{{ dataRetentionDesc1 }}</p>
          <p>{{ dataRetentionDesc2 }}</p>
        </li>
        <li>
          <strong>{{ rightsHeading }}</strong>
          <ul>
            <li>{{ rightsLine1 }}</li>
            <li>{{ rightsLine2 }}</li>
            <li>{{ rightsLine3 }}</li>
            <li>{{ rightsLine4 }}</li>
            <li>{{ rightsLine5 }}</li>
            <li>{{ rightsLine6 }}</li>
            <li>{{ rightsLine7 }}</li>
            <li>{{ rightsLine8 }}</li>
          </ul>
        </li>
        <li>
          <strong>{{ childrensHeading }}</strong>
          <p>{{ childrensDesc }}</p>
        </li>
        <li>
          <strong>{{ changesHeading }}</strong>
          <p>{{ changesDesc }}</p>
        </li>
        <li>
          <strong>{{ contactPrivacyHeading }}</strong>
          <p>{{ contactPrivacyDesc }}</p>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import BusinessTermsComponent from "@/components/Pages/BusinessTermsComponent.vue";

export default {
  name: "TermsPrivacy",
  components: { BusinessTermsComponent },
  setup() {
    const activeTab = ref("customers");
    const foregroundColor = "rgb(0,0,0)";
    const eligibilityHeading = "About Lulu Rewards and eligibility";
    const eligibilityLine1 = "Lulu Rewards is a digital loyalty platform operated by Lulu Rewards. We provide participating businesses with branded digital rewards cards that you can add to Apple Wallet or Google Wallet. The participating business that issues your Rewards card sets the terms of its own rewards programme, including how stamps, points, or rewards are earned and redeemed.";
    const eligibilityLine2 = "Rewards cards issued via the Lulu Rewards platform are available to individuals aged 18 and over. By registering for a Rewards card, you confirm that you meet this criterion.";
    const personalInfoHeading = "Personal information";
    const personalInfoLine1 = "By signing up, you consent to Lulu Rewards storing and processing the personal information required to provide the service. Lulu Rewards processes information about you on behalf of the participating business that issues your Rewards card; that business acts as the data controller, and Lulu Rewards acts as a data processor.";
    const personalInfoLine2 = "Your personal data will be handled in accordance with our Privacy Policy and applicable UK data protection laws, including the UK GDPR and the Data Protection Act 2018.";
    const earningHeading = "Earning and redeeming rewards";
    const earningLine1 = "Stamps, points, or other rewards are earned on qualifying purchases made at the participating business that issued your Rewards card, and may be redeemed as defined by that business. Please ask the participating business directly for the details of their rewards structure.";
    const earningLine2 = "Stamps, points, and rewards earned via the Lulu Rewards platform have no cash value and cannot be exchanged for cash.";
    const earningLine3 = "The participating business is solely responsible for honouring its rewards programme. Lulu Rewards is not liable for any failure or refusal of a participating business to issue or redeem stamps, points, or rewards, or for any dispute between you and a participating business about its rewards programme.";
    const earningLine4 = "Lulu Rewards reserves the right to modify or discontinue the platform service at any time, with reasonable notice where practicable.";
    const restrictionsHeading = "Rewards card use and restrictions";
    const restrictionsLine1 = "Your Rewards card is delivered digitally via Apple Wallet or Google Wallet. It is for personal use only and cannot be transferred, sold, or used for business transactions.";
    const restrictionsLine2 = "Fraudulent use of a Rewards card, including misuse of stamps or points or providing false information, may result in suspension or termination of your Rewards card by Lulu Rewards or by the participating business.";
    const terminationHeading = "Termination and modification";
    const terminationLine1 = "You may stop using your Rewards card at any time by removing the rewards card from your Apple Wallet or Google Wallet, or by contacting us at hello@lulurewards.co.uk.";
    const terminationLine2 = "If a participating business stops using the Lulu Rewards platform, your Rewards card with that business will no longer be active and any stamps, points, or rewards earned with that business may no longer be redeemable via the platform.";
    const terminationLine3 = "Lulu Rewards reserves the right to suspend or terminate your access to a Rewards card where we reasonably believe these Terms have been breached. Where a Rewards card is terminated, any unused stamps or points may be forfeited.";
    const liabilityHeading = "Liability and disclaimers";
    const liabilityLine1 = "Lulu Rewards is not responsible for lost, stolen, or unauthorised use of the Rewards Card.";
    const liabilityLine2 = "Save where prohibited by applicable UK consumer protection law, the Rewards Card is provided 'as is' without warranties of any kind.";
    const liabilityLine3 = "Subject to applicable law, Lulu shall not be liable for any losses resulting from system failures, unauthorised access, or force majeure events.";
    const liabilityLine4 = "Nothing in these Terms shall exclude or limit our liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for any other liability that cannot be excluded or limited under applicable UK law. This clause does not affect your statutory rights as a consumer.";
    const marketingHeading = "Marketing communications";
    const marketingLine1 = "When you sign up for a Rewards card, you can choose to receive marketing emails from the participating business that issues your Rewards card by ticking the optional box labelled \"Email me news, offers and review requests from [business name]\". These emails may include news, offers and requests to leave a review. Lulu Rewards may send these emails on that business's behalf and on its instructions; the participating business decides what is sent.";
    const marketingLine2 = "Ticking the box is optional and does not affect your Rewards card. You can withdraw your consent at any time by using the unsubscribe link in any marketing email, by asking the participating business, or by emailing us at hello@lulurewards.co.uk.";
    const marketingLine3 = "Service messages about your Rewards card, for example a notification that a reward is ready to redeem or that the participating business has added a stamp to your card, are part of the rewards programme and are not marketing.";
    const marketingLine4 = "Your marketing preferences will be handled in accordance with our Privacy Policy, the Privacy and Electronic Communications Regulations 2003 (PECR), and applicable UK data protection laws, including the UK GDPR.";
    const governingLawHeading = "Governing law";
    const governingLawLine1 = "These Terms and Conditions shall be governed by and construed in accordance with the laws of England and Wales.";
    const governingLawLine2 = "Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.";
    const contactHeading = "Contact information";
    const contactLine = "For any questions or concerns regarding these Terms and Conditions, please contact us at hello@lulurewards.co.uk";

    // Privacy Policy
    const privacyTitle = "Customer privacy policy";
    const effectiveDateLabel = "Effective date:";
    const effectiveDate = "8 October 2026";
    const infoCollectHeading = "Information we collect";
    const personalInfoLabel = "Personal information:";
    const personalInfoDesc = "When you register for a Rewards card or otherwise interact with our app, website, or rewards cards, we collect personal details such as your email address. We also record whether you ticked the optional marketing box at sign-up, together with the date and the version of the wording you saw when you made that choice.";
    const usageDataLabel = "Usage data:";
    const usageDataDesc = "We collect information about your use of our services, such as login activity, scan events, stamp redemptions, and device information.";
    const commDataLabel = "Communication data:";
    const commDataDesc = "If you contact us via email or phone we may collect the content of your communication.";
    const howUseHeading = "How we use your information";
    const howUseLine1 = "To provide, operate, and improve our services";
    const howUseLine2 = "To manage and track reward programmes";
    const howUseLine3 = "To send transaction-related notifications, updates, and support messages";
    const howUseLine4 = "To personalise and enhance your experience with our app";
    const howUseLine5 = "To send marketing emails, including review requests, on behalf of the participating business that issues your Rewards card, where you have consented (as outlined below)";
    const privacyMarketingHeading = "Marketing communications";
    const privacyMarketingDesc = "We only send you marketing emails if you chose to receive them by ticking the optional box labelled \"Email me news, offers and review requests from [business name]\" when you signed up for your Rewards card. These emails come from the participating business that issues your Rewards card, which decides what is sent; Lulu Rewards may send them on that business's behalf and on its instructions. They may include news, offers and requests to leave a review. Lulu Rewards does not send you marketing of its own. You can withdraw your consent at any time by using the unsubscribe link in any marketing email, by asking the participating business, or by emailing us at hello@lulurewards.co.uk. Service messages about your Rewards card, such as a notification that a reward is ready to redeem or that a stamp has been added to your card, are part of the rewards programme and are not marketing.";
    const sharingHeading = "Sharing your information";
    const sharingDesc = "We do not sell or rent your personal information to third parties. However, we may share your data with trusted service providers who assist us in providing the app and our service, such as payment processors, cloud storage providers, email marketing platforms, and analytics providers. Our analytics provider, Mixpanel, receives your email address as an identifier together with activity on your Rewards card, such as when a stamp is added or a reward is redeemed, so that we can understand how the service is used and fix problems. These providers are required to keep your information confidential and only use it for the services they provide to us.";
    const sharingCase1 = "To comply with legal obligations, such as responding to a court order or other valid legal request from a regulator or law enforcement authority";
    const sharingCase2 = "To protect our rights, property, or safety, or the rights, property, or safety of others";
    const dataRetentionHeading = "Data retention and security";
    const dataRetentionDesc1 = "We take reasonable measures to protect your personal information from unauthorised access, alteration, disclosure, or destruction. However, please be aware that no security system is completely impenetrable, and we cannot guarantee the absolute security of your information.";
    const dataRetentionDesc2 = "We retain your information for as long as necessary to fulfil the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.";
    const rightsHeading = "Your rights and choices";
    const rightsLine1 = "Access: You can request access to the personal information we hold about you.";
    const rightsLine2 = "Rectification: You can request that we correct any inaccurate or incomplete personal information we hold about you.";
    const rightsLine3 = "Erasure: You can request the deletion of your personal information, subject to certain limitations.";
    const rightsLine4 = "Restriction: You can request that we restrict the processing of your personal information in certain circumstances.";
    const rightsLine5 = "Data portability: You can request a copy of your personal information in a structured, commonly used and machine-readable format.";
    const rightsLine6 = "Object: You can object to the processing of your personal information for direct marketing, or where processing is based on our legitimate interests.";
    const rightsLine7 = "Opt-out of marketing communications: You can opt out of receiving marketing emails at any time by using the unsubscribe link in any marketing email or by contacting us at hello@lulurewards.co.uk.";
    const rightsLine8 = "Complaint: You have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk if you believe we have not handled your personal information in accordance with applicable UK data protection law.";
    const childrensHeading = "Children's privacy";
    const childrensDesc = "Our services are not intended for use by individuals under the age of 18. If we become aware that we have collected personal information from individuals under the age of 18, we will take steps to delete that information.";
    const changesHeading = "Changes to this privacy policy";
    const changesDesc = "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically to stay informed about how we are protecting your information.";
    const contactPrivacyHeading = "Contact us";
    const contactPrivacyDesc = "If you have any questions or concerns about this Privacy Policy or our privacy practices, please contact us at hello@lulurewards.co.uk.";

    return {
      activeTab,
      foregroundColor,
      eligibilityHeading,
      eligibilityLine1,
      eligibilityLine2,
      personalInfoHeading,
      personalInfoLine1,
      personalInfoLine2,
      earningHeading,
      earningLine1,
      earningLine2,
      earningLine3,
      earningLine4,
      restrictionsHeading,
      restrictionsLine1,
      restrictionsLine2,
      terminationHeading,
      terminationLine1,
      terminationLine2,
      terminationLine3,
      liabilityHeading,
      liabilityLine1,
      liabilityLine2,
      liabilityLine3,
      liabilityLine4,
      marketingHeading,
      marketingLine1,
      marketingLine2,
      marketingLine3,
      marketingLine4,
      governingLawHeading,
      governingLawLine1,
      governingLawLine2,
      contactHeading,
      contactLine,
      privacyTitle,
      effectiveDateLabel,
      effectiveDate,
      infoCollectHeading,
      personalInfoLabel,
      personalInfoDesc,
      usageDataLabel,
      usageDataDesc,
      commDataLabel,
      commDataDesc,
      howUseHeading,
      howUseLine1,
      howUseLine2,
      howUseLine3,
      howUseLine4,
      howUseLine5,
      privacyMarketingHeading,
      privacyMarketingDesc,
      sharingHeading,
      sharingDesc,
      sharingCase1,
      sharingCase2,
      dataRetentionHeading,
      dataRetentionDesc1,
      dataRetentionDesc2,
      rightsHeading,
      rightsLine1,
      rightsLine2,
      rightsLine3,
      rightsLine4,
      rightsLine5,
      rightsLine6,
      rightsLine7,
      rightsLine8,
      childrensHeading,
      childrensDesc,
      changesHeading,
      changesDesc,
      contactPrivacyHeading,
      contactPrivacyDesc,
    };
  },
};
</script>

<style scoped>
.terms-of-service {
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

/* Tab bar - matches HowLuluWorksComponent */
.tab-bar {
  display: flex;
  border-bottom: 2px solid rgba(0, 109, 62, 0.2);
  margin-bottom: 24px;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 15px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  font-family: 'Roboto', sans-serif;
}

.tab-btn:hover {
  color: rgb(0, 109, 62);
}

.tab-btn--active {
  color: rgb(0, 109, 62);
  border-bottom-color: rgb(0, 109, 62);
}

.tab-icon {
  font-size: 20px !important;
}

@media (max-width: 480px) {
  .tab-btn {
    font-size: 13px;
    padding: 10px 8px;
  }
}

.content {
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
}

h2 {
  font-size: 16px;
  margin-top: 24px;
  color: rgb(0,0,0);
}

ol,
ul {
  margin-left: 20px;
}

strong {
  font-weight: bold;
}

a {
  color: rgb(0,0,0);
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>